import QRCode from "react-qr-code";
import { Skeleton } from "@mui/material";
import { CircularProgress } from "@mui/material";
import intl from "react-intl-universal";
import { TbAlertTriangle, TbCopy } from "react-icons/tb";

export const DepositNetworkWarning = ({ networkLabel }) => (
  <div
    className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800/60 dark:bg-amber-950/30"
    role="alert"
  >
    <TbAlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
    <p className="text-sm text-amber-900 dark:text-amber-100">
      <span className="font-semibold">{intl.get("stables.depositNetworkWarningLead")}</span>{" "}
      {intl.get("stables.depositNetworkWarningBody")}
      {networkLabel ? (
        <>
          {" "}
          (<span className="font-semibold">{networkLabel}</span>)
        </>
      ) : null}
      . {intl.get("stables.depositNetworkWarningLoss")}
    </p>
  </div>
);

/**
 * Shared deposit address + QR panel used by IB Depositar and BaaS console Depositar.
 */
export const StablesDepositAddressPanel = ({
  assetLabel,
  networkLabel,
  address = "",
  hasAddress = false,
  loading = false,
  error = null,
  onRetry,
  onGenerate,
  generating = false,
  onCopy,
  emptyHint,
  generateLabel,
  generatingLabel,
  copyAriaLabel,
}) => {
  if (loading) {
    return (
      <div className="stables-deposit-panel mt-4">
        <div className="grid gap-6 py-4 md:grid-cols-[200px_1fr]">
          <Skeleton variant="rounded" height={200} sx={{ borderRadius: 1, bgcolor: "grey.100" }} />
          <div className="space-y-2">
            <Skeleton variant="text" width={140} height={12} />
            <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1, bgcolor: "grey.100" }} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stables-deposit-panel mt-4">
        <div className="app-empty-state">
          <p>{error}</p>
          {onRetry ? (
            <button type="button" className="btn-outline mt-2 !rounded-full !px-4" onClick={onRetry}>
              {intl.get("stables.retryLoad")}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!hasAddress) {
    return (
      <div className="stables-deposit-panel mt-4">
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <DepositNetworkWarning networkLabel={networkLabel} />
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {emptyHint ||
              intl.get("stables.depositNoAddressHint", {
                asset: assetLabel || "—",
                network: networkLabel || "—",
              })}
          </p>
          {onGenerate ? (
            <button
              type="button"
              className="btn-primary inline-flex min-w-[180px] items-center justify-center gap-2"
              onClick={onGenerate}
              disabled={generating}
            >
              {generating ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  {generatingLabel || intl.get("stables.depositGenerating")}
                </>
              ) : (
                generateLabel || intl.get("stables.depositGenerate")
              )}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="stables-deposit-panel mt-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {intl.get("stables.depositSelectedNetwork")}
          </p>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {networkLabel || "—"}
          </p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {intl.get("stables.depositSelectedAsset")}
          </p>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {assetLabel || "—"}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <DepositNetworkWarning networkLabel={networkLabel} />
        <div className="grid gap-6 md:grid-cols-[200px_1fr] md:items-start">
          <div className="stables-deposit-inset mx-auto flex w-full max-w-[200px] items-center justify-center p-4 md:mx-0">
            <QRCode value={address} size={168} />
          </div>

          <div className="min-w-0 w-full">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {intl.get("stables.depositAddressLabel")}
            </p>
            <div className="mt-1.5 flex w-full items-stretch gap-2">
              <code className="stables-code-block stables-deposit-address min-w-0 flex-1">
                {address}
              </code>
              {onCopy ? (
                <button
                  type="button"
                  className="btn-outline-icon !shrink-0 self-start"
                  onClick={() => onCopy(address)}
                  title={copyAriaLabel || intl.get("stables.depositCopyAddress")}
                  aria-label={copyAriaLabel || intl.get("stables.depositCopyAddress")}
                >
                  <TbCopy className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
